import React, { FunctionComponent } from "react";
import { Shimmer } from "sprint-common-web";

import {
    ensureAncillaryContentDetails,
    ensureFullTvSeriesContentDetails,
    ensureMovieContentDetails
} from "../../../../common/types/ContentDetails";
import { ContentSubtype } from "../../../../common/types/ContentSubtype";
import { PageHeader } from "../../../../common/ui/layout/header/PageHeader";
import { useCloudAssetDetails } from "../common/hooks/useCloudAssetDetails";
import { CloudAncillaryDetailsPageHeader } from "./ancillary/CloudAncillaryDetailsPageHeader";
import { CloudMovieDetailsPageHeader } from "./movie/CloudMovieDetailsPageHeader";
import { CloudTvSeriesDetailsPageHeader } from "./tv-series/CloudTvSeriesDetailsPageHeader";

interface CloudAssetDetailsPageHeaderProps {
    hideActions?: boolean;
}

export const CloudAssetDetailsPageHeader: FunctionComponent<CloudAssetDetailsPageHeaderProps> = (
    props?: CloudAssetDetailsPageHeaderProps
) => {
    const { data: assetDetails } = useCloudAssetDetails();

    if (!assetDetails) {
        return null;
    }

    const content = assetDetails.content;

    switch (content?.subtype) {
        case ContentSubtype.Movie:
        case ContentSubtype.TvSingle:
        case ContentSubtype.FeatureDocumentary:
        case ContentSubtype.ShortFilm: {
            ensureMovieContentDetails(content);

            return (
                <CloudMovieDetailsPageHeader
                    contentType={content.subtype}
                    movie={content.movie}
                    version={content.versionSummary}
                    cloudFileId={assetDetails.cloudFileId}
                    deliveryFileName={assetDetails.deliveryFileName}
                    filePath={assetDetails.filePath}
                    cloudAssetId={assetDetails.assetId}
                    hideActions={props?.hideActions}
                />
            );
        }

        case ContentSubtype.TV: {
            ensureFullTvSeriesContentDetails(content);

            return (
                <CloudTvSeriesDetailsPageHeader
                    tvSeries={content.tvSeries}
                    season={content.season}
                    episode={content.episode}
                    version={content.versionSummary}
                    cloudFileId={assetDetails.cloudFileId}
                    deliveryFileName={assetDetails.deliveryFileName}
                    filePath={assetDetails.filePath}
                    cloudAssetId={assetDetails.assetId}
                    hideActions={props?.hideActions}
                />
            );
        }

        case ContentSubtype.Advert:
        case ContentSubtype.Promo:
        case ContentSubtype.Safety:
        case ContentSubtype.ClientSpecific:
        case ContentSubtype.IndentWarning: {
            ensureAncillaryContentDetails(content);

            return (
                <CloudAncillaryDetailsPageHeader
                    contentType={content.subtype}
                    ancillary={content.ancillary}
                    airline={content.airline}
                    cloudFileId={assetDetails.cloudFileId}
                    deliveryFileName={assetDetails.deliveryFileName}
                    filePath={assetDetails.filePath}
                    cloudAssetId={assetDetails.assetId}
                    hideActions={props?.hideActions}
                />
            );
        }

        case undefined:
            return (
                <PageHeader>
                    <Shimmer width="60%" />
                </PageHeader>
            );

        default:
            return <></>;
    }
};
